import React from "react";
import { Divider, FormControlLabel, Grid } from "@material-ui/core";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { Controller, useWatch } from "react-hook-form";
import { EventNotification } from "../../domain/EventNotification";
import { SectionProps } from "./SectionProps";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  assessmentModificationTypeService,
  assessmentPaperTypeService,
  assessmentWhenIdentifiedService,
  assessmentWhoIdentifiedService,
  yesNoService,
  securityIncorrectPaperOutcomeService,
  securityMeansOfBreachService,
  ameAssessmentMethodService,
} from "../../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { fieldLabels } from "./fieldLabels";
import { nullOptionInjector } from "@ofqual/portal-core/core/utils/nullOptionInjector";

export const NatureOfEvent: React.FC<SectionProps<EventNotification>> = ({
  control,
  disabled,
  lookupTracker,
}) => {
  const { assessmentMaterialError, securityBreach } =
    useWatch<EventNotification>({
      control,
      name: ["assessmentMaterialError", "securityBreach"],
    });

  const [
    assessmentPaperTypeLookup,
    assessmentModificationTypeLookup,
    assessmentWhenIdentifiedLookup,
    assessmentWhoIdentifiedLookup,
    securityMeansOfBreachLookup,
    yesNoLookup,
    securityIncorrectPaperOutcomeLookup,
    ameAssessmentMethodLookup,
  ] = useLookup(
    nullOptionInjector(assessmentPaperTypeService),
    assessmentModificationTypeService,
    assessmentWhenIdentifiedService,
    assessmentWhoIdentifiedService,
    securityMeansOfBreachService,
    yesNoService,
    nullOptionInjector(securityIncorrectPaperOutcomeService),
    ameAssessmentMethodService
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="assessmentMaterialError"
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e: any) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  disabled={disabled}
                  data-test="ame-checkbox"
                />
              }
              label={fieldLabels["assessmentMaterialError"]}
            />
          )}
        />
      </Grid>
      {assessmentMaterialError && (
        <>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="amePaperType"
              as={
                <Select
                  label={fieldLabels["amePaperType"]}
                  readOnly={disabled}
                  datatest="ame-assessment-type"
                  lookupTracker={lookupTracker}
                >
                  {assessmentPaperTypeLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="eventNotificationAMEModificationType"
              as={
                <Select
                  label={fieldLabels.eventNotificationAMEModificationType}
                  readOnly={disabled}
                  selectVariant="multiple"
                  datatest="ame-modification-type"
                  lookupTracker={lookupTracker}
                >
                  {assessmentModificationTypeLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="eventNotificationAMEWhenIdentified"
              as={
                <Select
                  label={fieldLabels["eventNotificationAMEWhenIdentified"]}
                  readOnly={disabled}
                  selectVariant="multiple"
                  datatest="ame-when-identified"
                  lookupTracker={lookupTracker}
                >
                  {assessmentWhenIdentifiedLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="eventNotificationAMEWhoIdentified"
              as={
                <Select
                  label={fieldLabels["eventNotificationAMEWhoIdentified"]}
                  readOnly={disabled}
                  selectVariant="multiple"
                  datatest="ame-who-identified"
                  lookupTracker={lookupTracker}
                >
                  {assessmentWhoIdentifiedLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="amecategory"
              as={
                <Select
                  label={fieldLabels.amecategory}
                  readOnly={disabled}
                  datatest="ame-category"
                  lookupTracker={lookupTracker}
                >
                  {[
                    { id: null, label: "" },
                    { id: 1, label: "1" },
                    { id: 2, label: "2" },
                    { id: 3, label: "3" },
                  ]}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="eventNotificationAMEAssessmentMethod"
              as={
                <Select
                  label={fieldLabels["eventNotificationAMEAssessmentMethod"]}
                  readOnly={disabled}
                  selectVariant="multiple"
                  datatest="ame-assessment-method"
                  lookupTracker={lookupTracker}
                >
                  {ameAssessmentMethodLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Divider />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Controller
          control={control}
          name="securityBreach"
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e: any) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  disabled={disabled}
                  data-test="security-breach-checkbox"
                />
              }
              label={fieldLabels.securityBreach}
            />
          )}
        />
      </Grid>
      {securityBreach && (
        <>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="eventNotificationSecurityBreachMeans"
              as={
                <Select
                  label={fieldLabels.eventNotificationSecurityBreachMeans}
                  readOnly={disabled}
                  selectVariant="multiple"
                  datatest="security-breach-means"
                  lookupTracker={lookupTracker}
                >
                  {securityMeansOfBreachLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="securityBreachEvidenceOfTwoPeoplePresent"
              as={
                <Select
                  label={fieldLabels.securityBreachEvidenceOfTwoPeoplePresent}
                  readOnly={disabled}
                  datatest="security-breach-evidence"
                  lookupTracker={lookupTracker}
                >
                  {yesNoLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="securityBreachIncorrectPaperOutcome"
              as={
                <Select
                  label={fieldLabels.securityBreachIncorrectPaperOutcome}
                  readOnly={disabled}
                  datatest="security-breach-incorrect-paper"
                  lookupTracker={lookupTracker}
                >
                  {securityIncorrectPaperOutcomeLookup}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}></Grid>
        </>
      )}
    </Grid>
  );
};
